import React from "react";

interface DateParts {
  year: number;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

const pad = (value: number): string => String(value).padStart(2, "0");
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const YEAR_FIRST_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;
const MONTH_FIRST_TIMESTAMP = /^(\d{2})-(\d{2})-(\d{4})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

const isValidDateParts = (year: number, month: number, day: number, hour: number, minute: number, second: number): boolean => {
  if (month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59 || second > 59) return false;
  // Validate the calendar fields without constructing a Date, which could apply timezone rules.
  const daysInMonth = month === 2 && year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
    ? 29
    : DAYS_IN_MONTH[month - 1];
  return day <= daysInMonth;
};

const parseTimestampString = (value: string): DateParts | null => {
  // Read the timestamp text directly so formatting does not convert it to UTC or browser-local time.
  // Select the matching layout first so only one parser regex executes per value.
  const isYearFirst = value.length > 4 && value[4] === "-";
  const match = (isYearFirst ? YEAR_FIRST_TIMESTAMP : MONTH_FIRST_TIMESTAMP).exec(value);
  if (!match) return null;

  const year = Number(isYearFirst ? match[1] : match[3]);
  const month = Number(isYearFirst ? match[2] : match[1]);
  const day = Number(isYearFirst ? match[3] : match[2]);
  const hour = Number(match[4] ?? 0);
  const minute = Number(match[5] ?? 0);
  const second = Number(match[6] ?? 0);
  if (!isValidDateParts(year, month, day, hour, minute, second)) return null;

  return {
    year,
    month: pad(month),
    day: pad(day),
    hour: pad(hour),
    minute: pad(minute),
    second: pad(second),
  };
};

const getDateParts = (value: Date | number): DateParts | null => {
  // Date and number inputs are instants, so unlike strings they have no original text or timezone to preserve.
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : {
      year: date.getFullYear(),
      month: pad(date.getMonth() + 1),
      day: pad(date.getDate()),
      hour: pad(date.getHours()),
      minute: pad(date.getMinutes()),
      second: pad(date.getSeconds()),
    };
};

export default function formatDate(
  val: any,
  format?: "MM/DD/YYYY" | "MM-DD-YYYY" | "YYYY-MM-DD HH:MM:SS" | "MM-DD-YYYY HH:MM:SS",
): React.ReactNode {
  if (!val) return val;
  const parts = typeof val === "string" ? parseTimestampString(val) : getDateParts(val);
  if (!parts) return val;

  // If explicit format requested, honor it
  if (format === "MM/DD/YYYY") {
    return `${parts.month}/${parts.day}/${parts.year}`;
  }

  if (format === "MM-DD-YYYY") {
    return `${parts.month}-${parts.day}-${parts.year}`;
  }

  if (format === "YYYY-MM-DD HH:MM:SS") {
    return (
      <>
        {`${parts.year}-${parts.month}-${parts.day}`}
        <br />
        {`${parts.hour}:${parts.minute}:${parts.second}`}
      </>
    );
  }

  if (format === "MM-DD-YYYY HH:MM:SS") {
    return (
      <>
        {`${parts.month}-${parts.day}-${parts.year}`}
        <br />
        {`${parts.hour}:${parts.minute}:${parts.second}`}
      </>
    );
  }

  return val;
}
